/**
 * MotionActivityPopup — the "N 次活动" click popup for a Pipeline List row
 * (Figma 1956:24237 activity modal). A newest-first timeline of an Opp's
 * activities, fetched on open via GetOppActivities({ opptyId }).
 *
 * Each item (Figma): a left timeline dot + date, then a type icon + bold subject
 * + owner (@alias) on one line, then a muted comments line.
 *
 * ⚠️ date is day-precision only — activity_cache.activity_date is a SQL DATE with
 * no time-of-day. Figma shows seconds (06/05/2026 22:15:35) but there is no
 * source for it yet, so we render the date only (backend follow-up with DE).
 */

import { useEffect, useState } from 'react';
import { Box, Modal, Spinner } from '@amzn/awsui-components-react';
import { getIvtClient, GetOppActivitiesCommand, type MotionOppActivity } from '../../../client/IvtHubClient';
import { ActivityIcon } from '../../../components/ActivityIcon/ActivityIcon';
import './MotionActivityPopup.css';

interface MotionActivityPopupProps {
  opptyId: string;
  /** Popup title = "{title} (N 次活动[（M 次 IVT）])" — account/opp name + counts. */
  title: string;
  count: number;
  /** IVT-owned subset of `count` (activities whose owner is an IVT org member).
   *  Undefined (old backend) → the "（M 次 IVT）" suffix is omitted. */
  ivtCount?: number;
  onClose: () => void;
}

/**
 * Activity-count label shared by the Pipeline List trigger, the Motion detail
 * trigger, and this popup's title: "N 次活动" plus "（M 次 IVT）" when the IVT
 * subset count is present. One place so the three sites never drift. ivtCount
 * undefined (pre-CR-287668785 backend) → suffix omitted (graceful).
 */
export function activityCountLabel(count: number, ivtCount?: number): string {
  return ivtCount == null ? `${count} 次活动` : `${count} 次活动（${ivtCount} 次 IVT）`;
}

export default function MotionActivityPopup({ opptyId, title, count, ivtCount, onClose }: MotionActivityPopupProps) {
  const [activities, setActivities] = useState<MotionOppActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new GetOppActivitiesCommand({ opptyId }));
        if (!cancelled) setActivities(out.activities ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opptyId]);

  return (
    <Modal visible onDismiss={onClose} header={`${title} (${activityCountLabel(count, ivtCount)})`}>
      {error ? (
        <Box color="text-status-error">⚠️ {error}</Box>
      ) : activities === null ? (
        <Box padding={{ vertical: 'l' }} textAlign="center">
          <Spinner /> 加载活动…
        </Box>
      ) : activities.length === 0 ? (
        <Box padding={{ vertical: 'l' }} textAlign="center" color="text-body-secondary">
          该 Opp 暂无活动记录。
        </Box>
      ) : (
        <ol className="ivt-activity-timeline">
          {activities.map((a) => (
            <li className="ivt-activity" key={a.activityId}>
              <span className="ivt-activity__dot" aria-hidden="true" />
              <div className="ivt-activity__body">
                <div className="ivt-activity__date">{a.date ?? '—'}</div>
                <div className="ivt-activity__head">
                  <ActivityIcon type={a.activityType} />
                  {a.isIvt && (
                    <span className="ivt-activity__ivt-tag" title="IVT 团队成员的活动">
                      [IVT]
                    </span>
                  )}
                  <span className="ivt-activity__subject" title={a.bdActivityType ?? undefined}>
                    {a.subject ?? '—'}
                  </span>
                  {a.ownerAlias && <span className="ivt-activity__owner">@{a.ownerAlias}</span>}
                  {a.status && (
                    <span
                      className={`ivt-activity__status${
                        a.status.toLowerCase() === 'completed' ? ' ivt-activity__status--done' : ''
                      }`}
                    >
                      {a.status}
                    </span>
                  )}
                </div>
                {a.comments && <div className="ivt-activity__comments">{a.comments}</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
